import _ from 'lodash'

import { Dictionary } from '../../utils/types'

import {
  Banner,
  CompanyDetailPageUmbracoConfig,
  ContentConfiguration,
  Link,
  SearchWidgetDisplayMode,
  Seo,
  UmbracoImage,
  CountryFilter,
  OfficialDocuments
} from './types'

export type CompanyDetailPageUmbracoConfigResponse = {
  seo?: SeoResponse
  banner: BannerResponse
  roleVisibility: string[]
  officialDocuments?: OfficialDocumentsResponse
  canShowPriceForAnonymousUsers: boolean
}
export type InvestigationUmbracoConfigResponse = {
  countries: string[]
}
export type SeoResponse = {
  metaTitle: string
  pageTitle: string
  keywords: string[]
  metaDescription: string
}

export type UmbracoLinkResponse = {
  url: string
  text: string
  target: string
}

export type UmbracoNavigationLinkResponse = {
  linkModel?: UmbracoLinkResponse
  dropdownLinksMenuModel?: {
    header: string
    links: UmbracoLinkResponse[]
  }
}

export type UmbracoCountryFilter = {
  label: string
  countryCode: string
}

export type BannerResponse = {
  id: string
  title: string
  description: string
  link?: UmbracoLinkResponse
  textColor: string
  image?: UmbracoImageResponse
  backgroundColor?: string
  icon?: string
}

export type OfficialDocumentsResponse = {
  asynchronousLoading: boolean
  asynchronousDocumentsCountries?: string[]
  infoBoxTitle?: string
  infoBoxDescription?: string
  infoBoxCtaTitle?: string
  infoBoxPromo?: string
}

export type ContactUsReason = {
  domainType: string
  translations: Dictionary<string>
}

export type ContentConfigurationResponse = {
  cmsHomeUrl: string
  cmsFaqUrl: string
  cmsLoginUrl: string
  cmsContactsUrl: string
  externalSearchLink: UmbracoLinkResponse
  searchWidgetDisplayMode: 'WithFilters' | 'WithoutFilters'
  searchFilters: Array<{
    label: string
    identifier: string
    placeholder?: string
    countries?: Array<{ label: string; countryCode: string }>
  }>
  countryFilterEnabled: boolean
  defaultCountryFilter: UmbracoCountryFilter
  otherCountriesFilter: Array<{ label: string; countryCode: string }>
  navigationLinks: UmbracoNavigationLinkResponse[]
  footerLinks: UmbracoLinkResponse[]
  footerText: string
  logoInverted?: UmbracoImageResponse
  logo?: UmbracoImageResponse
  mobileLogoInverted?: UmbracoImageResponse
  mobileLogo?: UmbracoImageResponse
  sessionTimeout?: number
  headerBanner?: BannerResponse
  mostRecentUpdatePolicyDate?: string
  showBulkImportMenuInHeader?: boolean
  showBulkImportLinkAboveSearchButton?: boolean
  showAsBulkImportButton?: boolean
  contactWidget?: BannerResponse
  showMulticolumnDropdown?: boolean
  showContactUsFormWidget?: boolean
  contactUsReason?: ContactUsReason
  preferredProductsEnabled: boolean | undefined
  showAiChatbotButton?: boolean
  sapAnonymousUserId?: string
  showAIColumsOnArchive?: boolean
  selfRegistrationEnabled?: boolean
  showAvailableBalance?: boolean
}

export type UmbracoImageResponse = {
  url: string
  altText?: string
}

export function mapUmbracoLink(respLink?: UmbracoLinkResponse): Link | undefined {
  if (!respLink) return undefined

  return {
    url: respLink.url,
    text: respLink.text,
    target: respLink.target
  }
}

export function mapUmbracoImage(respImg?: UmbracoImageResponse): UmbracoImage | undefined {
  if (!respImg) return undefined

  return {
    url: respImg.url,
    alt: respImg.altText
  }
}

export function mapUmbracoCountryFilter(respCountryFilter?: UmbracoCountryFilter): CountryFilter | undefined {
  if (!respCountryFilter) return undefined

  return {
    label: respCountryFilter.label,
    countryCode: respCountryFilter.countryCode
  }
}

export function mapUmbracoBanner(resp?: BannerResponse): Banner | undefined {
  return (
    resp && {
      id: resp.id,
      title: resp.title,
      description: resp.description,
      image: mapUmbracoImage(resp.image),
      backgroundColor: resp.backgroundColor,
      textColor: resp.textColor,
      link: mapUmbracoLink(resp.link),
      icon: resp.icon
    }
  )
}

export function mapContentConfigurationResponse(resp: ContentConfigurationResponse): ContentConfiguration {
  return {
    cmsHomeUrl: resp.cmsHomeUrl,
    cmsFaqUrl: resp.cmsFaqUrl,
    cmsLoginUrl: resp.cmsLoginUrl,
    cmsContactsUrl: resp.cmsContactsUrl,
    externalSearchLink: mapUmbracoLink(resp.externalSearchLink),
    footerText: resp.footerText,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    footerLinks: _.map(resp.footerLinks, l => mapUmbracoLink(l)!),
    searchWidgetDisplayMode: resp.searchWidgetDisplayMode as SearchWidgetDisplayMode,
    searchFilters: _.map(resp.searchFilters, f => ({
      label: f.label,
      identifier: f.identifier,
      placeholder: f.placeholder,
      countries: _.map(f.countries, c => ({ label: c.label, countryCode: c.countryCode }))
    })),
    countryFilterEnabled: resp.countryFilterEnabled,
    defaultCountryFilter: mapUmbracoCountryFilter(resp.defaultCountryFilter),
    otherCountriesFilter: _.map(resp.otherCountriesFilter, c => ({
      label: c.label,
      countryCode: c.countryCode
    })),
    navigationLinks: _.map(resp.navigationLinks, nl => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      linkModel: nl.linkModel ? mapUmbracoLink(nl.linkModel) : undefined,
      dropdownLinksMenuModel: nl.dropdownLinksMenuModel
        ? {
            header: nl.dropdownLinksMenuModel.header,
            links: _.compact(_.map(nl.dropdownLinksMenuModel.links, mapUmbracoLink)) ?? []
          }
        : undefined
    })),
    logo: mapUmbracoImage(resp.logo),
    logoInverted: mapUmbracoImage(resp.logoInverted),
    mobileLogoInverted: mapUmbracoImage(resp.mobileLogoInverted),
    mobileLogo: mapUmbracoImage(resp.mobileLogo),
    sessionTimeout: resp.sessionTimeout,
    headerBanner: mapUmbracoBanner(resp.headerBanner),
    showBulkImportMenuInHeader: resp.showBulkImportMenuInHeader,
    showBulkImportLinkAboveSearchButton: resp.showBulkImportLinkAboveSearchButton,
    showAsBulkImportButton: resp.showAsBulkImportButton,
    contactWidget: mapUmbracoBanner(resp.contactWidget),
    showMulticolumnDropdown: resp.showMulticolumnDropdown,
    showContactUsFormWidget: resp.showContactUsFormWidget,
    contactUsReason: resp.contactUsReason,
    preferredProductsEnabled: resp.preferredProductsEnabled,
    showAiChatbotButton: resp.showAiChatbotButton,
    sapAnonymousUserId: resp.sapAnonymousUserId,
    showAIColumsOnArchive: resp.showAIColumsOnArchive,
    selfRegistrationEnabled: resp.selfRegistrationEnabled,
    showAvailableBalance: resp.showAvailableBalance
  }
}

export function mapSeoResponse(resp?: SeoResponse): Seo | undefined {
  return (
    resp && {
      keywords: resp.keywords,
      metaDescription: resp.metaDescription,
      metaTitle: resp.metaTitle,
      pageTitle: resp.metaTitle
    }
  )
}

export function mapOfficialDocuments(resp?: OfficialDocumentsResponse): OfficialDocuments | undefined {
  return {
    asyncLoadingCountries: resp?.asynchronousDocumentsCountries ?? [],
    infoBoxTitle: resp?.infoBoxTitle,
    infoBoxDescription: resp?.asynchronousLoading ? resp.infoBoxDescription : undefined,
    infoBoxCtaTitle: resp?.asynchronousLoading ? resp.infoBoxCtaTitle : undefined,
    infoBoxPromo: resp?.infoBoxPromo
  }
}

export function mapCompanyDetailPageUmbracoConfig(
  resp?: CompanyDetailPageUmbracoConfigResponse
): CompanyDetailPageUmbracoConfig | undefined {
  return (
    resp && {
      banner: mapUmbracoBanner(resp.banner),
      roleVisibility: resp.roleVisibility,
      seo: mapSeoResponse(resp.seo),
      officialDocuments: mapOfficialDocuments(resp.officialDocuments),
      canShowPriceForAnonymousUsers: resp.canShowPriceForAnonymousUsers
    }
  )
}
